import React from 'react'
import { UnionToTuple } from 'type-fest'

import {
	accentLight,
	accentLight30,
	accentLight60,
	accentLightShadow,
	accentPrimary,
	accentTertiary,
	bgMaterial,
	bgMaterialAlt,
	bgMaterialAlt70,
	black10,
	boxShadowFloat,
	currentColor,
	fgContrast,
	fgPrimary,
	fgPrimary30,
	fgPrimary40,
	fgPrimary50,
	transparent,
} from '@/_vars'
import { MobileClickArea } from '@/atoms/MobileClickArea'
import { TestId } from '@/lib/e2e-utils'
import {
	backgroundGray,
	blue700,
	brightRed,
	darkRed,
	darkTurquoise,
	disabledGray,
	fontBlack,
	hoverBlue,
	linkBlue,
	purple,
	secondaryBlack,
	white,
	yellow,
} from '@/refactor/colors'
import { font, FontSize, FontWeight } from '@/refactor/fonts'
import styled, { css, flex, muiUtils, padMulti, padPx, size } from '@/styles'

type PaddingSizes = 'xs' | 'sm' | 'md' | 'lg' | 'rounded'

export interface IProps
	extends
		React.PropsWithChildren<Partial<TestId>>,
		Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'onMouseOver' | 'onMouseOut'> {
	onClick?: React.MouseEventHandler<HTMLButtonElement>
	title?: string
	style?: React.CSSProperties
	className?: string
	color?: Colors
	rounded?: boolean | number | string
	paddingSize?: PaddingSizes | number[]
	mobilePaddingSize?: PaddingSizes | number[]
	fullWidth?: boolean
	noWrap?: boolean
	width?: number | string
	height?: number
	fontSize?: FontSize
	fontWeight?: FontWeight
	disabled?: boolean
	minWidth?: number
	withoutBorder?: boolean
	withoutShadow?: boolean
	hideMobileClickArea?: boolean
	isActive?: boolean
}

const PaddingMap: Record<PaddingSizes | 'rounded', number[]> = {
	xs: [4, 14],
	sm: [8, 20],
	md: [14, 28],
	lg: [17, 32],
	rounded: [10, 20],
}

const FontSizeMap: Record<PaddingSizes | 'rounded', FontSize> = {
	xs: 12,
	sm: 14,
	md: 16,
	lg: 18,
	rounded: 14,
}

const BackgroundColorMap = {
	primary: accentPrimary,
	primaryLight: accentLight,
	negative: brightRed,
	negativeTransparent: transparent,
	secondary: bgMaterial,
	tertiary: accentTertiary,
	dark: fgPrimary40,
	primaryInverse: 'none',
	background: bgMaterial,
	activeFilter: linkBlue,
	transparent,
	menuItem: transparent,
	menuItemError: transparent,
	yellow: yellow,
	purple: purple,
	linkBlue: linkBlue,
	disabled: disabledGray,
	transparentBlue: bgMaterial,
	bluePrimary: linkBlue,
	minor: white,
	ghost: transparent,
	blueOutline: transparent,
	noColor: transparent,
	whiteOutline: bgMaterial,
}

const HoverBackgroundColorMap: Partial<Record<Colors, string>> = {
	primary: darkTurquoise,
	primaryLight: accentLight,
	negative: darkRed,
	negativeTransparent: brightRed,
	secondary: backgroundGray,
	tertiary: accentTertiary, // TODO: update
	dark: fgPrimary30,
	primaryInverse: accentLight30,
	background: bgMaterialAlt,
	activeFilter: linkBlue,
	transparent: accentPrimary,
	menuItem: darkTurquoise,
	menuItemError: darkRed,
	yellow: '#D69418',
	purple: purple,
	linkBlue: linkBlue,
	disabled: disabledGray,
	transparentBlue: linkBlue,
	bluePrimary: blue700,
	minor: hoverBlue,
	ghost: hoverBlue,
	blueOutline: backgroundGray,
	noColor: transparent,
	whiteOutline: hoverBlue,
}

const ActiveBackgroundColorMap: Partial<Record<Colors, string>> = {
	primary: darkTurquoise,
	primaryLight: accentLightShadow,
	negative: darkRed,
	negativeTransparent: darkRed,
	secondary: backgroundGray,
	tertiary: accentTertiary, // TODO: update
	dark: fgPrimary50,
	primaryInverse: accentLight60,
	background: bgMaterialAlt70,
	activeFilter: linkBlue,
	transparent: black10,
	menuItem: darkTurquoise,
	menuItemError: darkRed,
	yellow: '#D69418',
	purple: purple,
	linkBlue: linkBlue,
	disabled: disabledGray,
	transparentBlue: linkBlue,
	bluePrimary: blue700,
	minor: hoverBlue,
	ghost: hoverBlue,
	blueOutline: backgroundGray,
	noColor: transparent,
	whiteOutline: hoverBlue,
}

const ForegroundColorMap: Record<Colors, string> = {
	primary: fgContrast,
	primaryLight: fgContrast,
	negative: fgContrast,
	negativeTransparent: fgPrimary,
	secondary: fontBlack,
	tertiary: fgContrast,
	dark: fgContrast,
	primaryInverse: accentPrimary,
	background: fgPrimary,
	activeFilter: white,
	transparent: currentColor,
	menuItem: currentColor,
	menuItemError: currentColor,
	yellow: fgContrast,
	purple: fgContrast,
	linkBlue: fgContrast,
	disabled: fgContrast,
	transparentBlue: linkBlue,
	bluePrimary: fgContrast,
	minor: linkBlue,
	ghost: linkBlue,
	blueOutline: linkBlue,
	noColor: currentColor,
	whiteOutline: fontBlack,
}

const BorderColorMap: {
	[K in Colors]?: string
} = {
	secondary: secondaryBlack,
	transparentBlue: linkBlue,
	blueOutline: linkBlue,
	menuItem: transparent,
	whiteOutline: secondaryBlack,
}

const HoverBorderColorMap: {
	[K in Colors]?: string
} = {
	secondary: secondaryBlack,
	blueOutline: linkBlue,
	whiteOutline: secondaryBlack,
}

const ActiveBorderColorMap: {
	[K in Colors]?: string
} = {
	secondary: secondaryBlack,
	blueOutline: linkBlue,
	whiteOutline: secondaryBlack,
}

const DisabledBorderColorMap: {
	[K in Colors]?: string
} = {
	blueOutline: secondaryBlack,
	whiteOutline: secondaryBlack,
}

const DisabledBackgroundColorMap: {
	[K in Colors]?: string
} = {
	blueOutline: transparent,
}

function isPaddingSize(key: PaddingSizes | number[] | undefined): key is PaddingSizes {
	return typeof key === 'string' && key in PaddingMap
}

export type Colors = keyof typeof BackgroundColorMap
export const ButtonColorNames = Object.keys(BackgroundColorMap) as UnionToTuple<Colors>

const StyledButton = styled.button<IProps>`
	${({ disabled: $disabled, color: $color, noWrap: $noWrap }) => css`
		cursor: ${!$disabled && 'pointer'};
		position: relative;

		color: ${$color ? ForegroundColorMap[$color] : ''};
		background: ${$color ? BackgroundColorMap[$color] : ''};
		${$noWrap &&
		css`
			white-space: nowrap;
		`};
	`}
`

export const ButtonBase = React.forwardRef<HTMLButtonElement, IProps>(function ButtonBase(
	{ children, hideMobileClickArea, ...rest },
	ref,
) {
	return (
		<StyledButton {...rest} ref={ref}>
			{hideMobileClickArea ? null : <MobileClickArea />}
			{children}
		</StyledButton>
	)
})

const Button = styled(ButtonBase).attrs<IProps>((p) => ({
	color: p.color ?? 'secondary',
	paddingSize: p.paddingSize ?? 'md',
}))`
	${({
		withoutBorder: $withoutBorder,
		color: $color,
		rounded: $rounded,
		paddingSize: $paddingSize,
		mobilePaddingSize: $mobilePaddingSize,
		fontSize: $fontSize,
		fontWeight: $fontWeight,
		noWrap: $noWrap,
		fullWidth: $fullWidth,
		width: $width,
		height: $height,
		disabled: $disabled,
		withoutShadow: $withoutShadow,
		isActive: $isActive,
		minWidth: $minWidth,
	}) => css`
		border: ${$withoutBorder ? 'none' : `1px solid ${BorderColorMap[$color!] || BackgroundColorMap[$color!]}`};
		${() => {
			switch ($color) {
				case 'blueOutline':
				case 'whiteOutline':
					return css`
						border-width: 2px;
					`
				default:
					return undefined
			}
		}}

		padding: ${$rounded
			? isPaddingSize($paddingSize)
				? padMulti(PaddingMap[$paddingSize!])
				: PaddingMap.rounded
			: isPaddingSize($paddingSize)
				? padMulti(PaddingMap[$paddingSize!])
				: padMulti($paddingSize ?? PaddingMap.md)};

		${muiUtils.mobile} {
			padding: ${$mobilePaddingSize
				? $rounded
					? isPaddingSize($mobilePaddingSize)
						? padMulti(PaddingMap[$mobilePaddingSize!])
						: PaddingMap.rounded
					: isPaddingSize($mobilePaddingSize)
						? padMulti(PaddingMap[$mobilePaddingSize!])
						: padMulti($mobilePaddingSize ?? PaddingMap.md)
				: ''};

			min-height: ${$rounded ? '50px' : '40px'};
		}

		${font({
			size:
				$fontSize ??
				($rounded ? FontSizeMap.rounded : isPaddingSize($paddingSize) ? FontSizeMap[$paddingSize!] : FontSizeMap.md),
			weight: $fontWeight ?? 'bold',
			textWrap: $noWrap ? 'nowrap' : undefined,
		})}
		border-radius: ${padPx(typeof $rounded === 'boolean' && $rounded ? 30 : $rounded || 5)};
		line-height: 1.3;
		cursor: pointer;
		transition: all 150ms ease-in-out;
		${$fullWidth ? 'width: 100%;' : ''}
		${$width ? `width: ${padPx($width)};` : ''}
	${$height ? `height: ${padPx($height)};` : ''}

	${['transparent', 'negativeTransparent', 'transparentBlue'].includes($color!)
			? `
		border-color: currentColor;

		${
			!$disabled
				? css`
						&:hover {
							color: white;
							border-color: ${HoverBackgroundColorMap[$color!]};
						}

						&:active {
							color: ${HoverBackgroundColorMap[$color!]};
						}
					`
				: ''
		}
	`
			: ''}

	${$disabled
			? css`
					color: ${secondaryBlack};
					cursor: default;
					background: ${DisabledBackgroundColorMap[$color!] || disabledGray};
					border-color: ${DisabledBorderColorMap[$color!] || disabledGray};
				`
			: css`
					&:hover {
						background-color: ${HoverBackgroundColorMap[$color!]};
						border-color: ${HoverBorderColorMap[$color!] || HoverBackgroundColorMap[$color!]};
						box-shadow: ${!$withoutShadow && boxShadowFloat};
					}

					&:active {
						background-color: ${ActiveBackgroundColorMap[$color!]};
						border-color: ${ActiveBorderColorMap[$color!] || HoverBackgroundColorMap[$color!]};
					}
				`}

	${!$disabled && $isActive
			? css`
					background-color: ${HoverBackgroundColorMap[$color!]};
					border-color: ${HoverBorderColorMap[$color!] || HoverBackgroundColorMap[$color!]};
					box-shadow: ${!$withoutShadow && boxShadowFloat};
				`
			: ''}

	&:focus {
			outline: none;
		}

		${$minWidth ? `min-width: ${padPx($minWidth)};` : ''}
	`}
`

Button.displayName = 'Button'

export type RoundedButtonProps = IProps & {
	diameter?: number | string
	fontSize?: FontSize
	borderRadius?: number
}

export const RoundedButton = styled(Button)<RoundedButtonProps>`
	${({ diameter: $diameter, borderRadius: $borderRadius, fontSize: $fontSize }) => css`
		${$diameter ? size($diameter) : ''}
		border-radius: ${$borderRadius ?? 5}px;
		${$fontSize ? font({ size: $fontSize }) : font({ size: 12 })};
	`}
`

export const IconButton = styled(ButtonBase)`
	flex: 0 0 auto;
	text-align: center;
	border: 0;
	padding: 0;
	background: transparent;
	cursor: pointer;
	transition: all 150ms ease-in-out;
	display: flex;

	${({ disabled: $disabled }) =>
		$disabled
			? `
	cursor: default;
	opacity: 0.4;
	`
			: `
			&:hover {
				color: ${linkBlue};
			}`}
`

export const TextButton = styled(ButtonBase)<{ fontColor?: string; suppressHoverColor?: boolean }>`
	${({ fontColor: $fontColor, suppressHoverColor: $suppressHoverColor }) => css`
		padding: 0;

		color: ${$fontColor ?? ''};

		&[disabled] {
			color: ${disabledGray};
		}

		&:not([disabled]):hover {
			color: ${$suppressHoverColor ? '' : linkBlue};
		}

		border: none;
		background: none;

		transition: all 150ms ease-in-out;

		&:hover {
			background: none;
			box-shadow: none;
		}
	`}
`

export const FlexButton = styled(Button)`
	${(p) => flex({ justify: 'center', align: 'center', gap: p.theme.spacing() })};
`

export type FlexButtonProps = React.ComponentPropsWithRef<typeof FlexButton>

export const LinkButton = styled(TextButton).attrs({ fontColor: linkBlue })``

export default Button
